import { ArrowRight, XCircle, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/hooks/useLanguage";

const WarumPlattformSection = () => {
  const { ref, isVisible } = useScrollAnimation();
  const { t, tObject, localizePath } = useLanguage();
  const problems = tObject<string[]>('aiAgentPlattform.warum.problems');
  const solutions = tObject<string[]>('aiAgentPlattform.warum.solutions');

  return (
    <section 
      ref={ref as React.RefObject<HTMLElement>}
      className={cn(
        "py-16 md:py-24 bg-secondary/50 transition-all duration-700 ease-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge variant="outline" className="mb-4 text-sm font-semibold text-primary uppercase tracking-wider">{t('aiAgentPlattform.warum.badge')}</Badge>
          
            <h2 className="text-3xl md:text-4xl font-semibold mb-2">
              <span className="text-foreground">{t('aiAgentPlattform.warum.titleLine1')}</span>
              <br />
              <span className="text-foreground">{t('aiAgentPlattform.warum.titleLine2')}</span>
            </h2>
          </div>

          <p className="text-lg text-muted-foreground mb-10 max-w-3xl mx-auto text-center">
            {t('aiAgentPlattform.warum.intro')}
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-10">
            <div 
              className={cn(
                "bg-background rounded-2xl p-8 md:p-10 border border-border transition-all duration-500",
                isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
              )}
              style={{ transitionDelay: isVisible ? "200ms" : "0ms" }}
            >
              <h3 className="text-lg font-semibold text-primary mb-6 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-chathero-orange/10 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-chathero-orange" />
                </span>
                {t('aiAgentPlattform.warum.problemTitle')}
              </h3>
              <ul className="space-y-5">
                {problems.map((problem, index) => (
                  <li 
                    key={index}
                    className={cn(
                      "flex items-start gap-3 transition-all",
                      isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
                    )}
                    style={{ transitionDelay: isVisible ? `${300 + index * 100}ms` : "0ms", transitionDuration: "400ms" }}
                  >
                    <span className="w-2 h-2 rounded-full bg-chathero-orange/40 flex-shrink-0 mt-2" />
                    <span className="text-muted-foreground">{problem}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div 
              className={cn(
                "bg-white rounded-2xl p-8 md:p-10 border border-chathero-mint/30 shadow-[0_4px_24px_rgba(0,196,180,0.08)] transition-all duration-500",
                isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
              )}
              style={{ transitionDelay: isVisible ? "200ms" : "0ms" }}
            >
              <h3 className="text-lg font-semibold text-primary mb-6 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-chathero-mint/10 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-chathero-mint" />
                </span>
                {t('aiAgentPlattform.warum.solutionTitle')}
              </h3>
              <ul className="space-y-5">
                {solutions.map((solution, index) => (
                  <li 
                    key={index}
                    className={cn(
                      "flex items-start gap-3 transition-all",
                      isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"
                    )}
                    style={{ transitionDelay: isVisible ? `${300 + index * 100}ms` : "0ms", transitionDuration: "400ms" }}
                  >
                    <span className="w-2 h-2 rounded-full bg-chathero-mint/40 flex-shrink-0 mt-2" />
                    <span className="text-muted-foreground">{solution}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div 
            className={cn(
              "bg-chathero-mint/10 border-l-4 border-chathero-mint rounded-r-xl p-6 md:p-8 mb-8 transition-all duration-500",
              isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
            )}
            style={{ transitionDelay: isVisible ? "600ms" : "0ms" }}
          >
            <p className="text-xl md:text-2xl font-semibold text-primary">
              {t('aiAgentPlattform.warum.highlight')}
            </p>
          </div>

          <Link 
            to={localizePath("/produkte/ai-agent")}
            className={cn(
              "inline-flex items-center gap-2 text-chathero-mint font-semibold hover:gap-3 transition-all",
              isVisible ? "opacity-100" : "opacity-0"
            )}
            style={{ transitionDelay: isVisible ? "700ms" : "0ms" }}
          >
            {t('aiAgentPlattform.warum.linkText')}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default WarumPlattformSection;
